import { useRef } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { Check, FileText, Lock } from "lucide-react";

const amount = 48730;
const brl = (n) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Ilustração: uma duplicata vira caixa no app. Os dados são fictícios e a legenda diz isso.
// Em loop; para quem pede menos movimento, fica parada no estado final.
export default function PhoneDemo() {
  const root = useRef(null);

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.set(q(".ph-step"), { autoAlpha: 0 });
        gsap.set(q(".ph-done"), { autoAlpha: 1 });
        gsap.set(q(".float"), { autoAlpha: 1 });
        q(".ph-amount")[0].textContent = brl(amount);
      });

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const out = q(".ph-amount")[0];
        const counter = { v: 0 };
        const ease = "power3.out";

        gsap.set(q(".ph-step"), { autoAlpha: 0, y: 16 });
        gsap.set(q(".float"), { autoAlpha: 0, y: 12 });
        gsap.set(q(".ph-bar i"), { scaleX: 0 });

        // Só roda com o celular na tela: fora dela, o loop pausa
        const tl = gsap
          .timeline({
            repeat: -1,
            repeatDelay: 0.6,
            delay: 0.8,
            scrollTrigger: { trigger: root.current, start: "top bottom", end: "bottom top", toggleActions: "play pause resume pause" },
          })
          // 1. Chega a duplicata
          .to(q(".ph-card"), { autoAlpha: 1, y: 0, duration: 0.7, ease })
          // 2. Toque em Antecipar
          .to(q(".ph-cta"), { scale: 0.96, duration: 0.14, ease: "power2.out" }, "+=1.1")
          .to(q(".ph-cta"), { scale: 1, duration: 0.3, ease })
          .to(q(".float-right"), { autoAlpha: 1, y: 0, duration: 0.6, ease }, "<")
          // 3. Processando
          .to(q(".ph-card"), { autoAlpha: 0, y: -16, duration: 0.4, ease: "power2.in" }, "+=0.1")
          .to(q(".ph-progress"), { autoAlpha: 1, y: 0, duration: 0.5, ease })
          .to(q(".ph-bar i"), { scaleX: 1, duration: 1.4, ease: "power2.inOut" }, "<")
          .to(q(".float-left"), { autoAlpha: 1, y: 0, duration: 0.6, ease }, "<0.6")
          // 4. Recebido
          .to(q(".ph-progress"), { autoAlpha: 0, y: -16, duration: 0.4, ease: "power2.in" })
          .fromTo(q(".ph-done"), { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.6, ease })
          .fromTo(
            counter,
            { v: 0 },
            {
              v: amount,
              duration: 1.1,
              ease: "power2.out",
              onUpdate: () => (out.textContent = brl(Math.round(counter.v))),
            },
            "<"
          )
          // 5. Pausa e recomeço
          .to(q(".ph-done, .float"), { autoAlpha: 0, duration: 0.5, ease: "power2.in" }, "+=2.6")
          .set(q(".ph-bar i"), { scaleX: 0 })
          .set(q(".ph-step"), { y: 16 });
        tl.timeScale(1.3);
      });
    },
    { scope: root }
  );

  return (
    <figure className="phone-stage" ref={root} aria-label="Ilustração: uma duplicata sendo antecipada pelo app">
      <div className="phone" data-tilt>
        <div className="phone-screen" aria-hidden="true">
          <div className="ph-status">
            <span>9:41</span>
            <span className="ph-notch" />
            <span>5G</span>
          </div>
          <div className="ph-head">
            <img src="/finball.png" alt="" width="28" height="28" />
            <div>
              <small>Olá,</small>
              <strong>Indústria Exemplo Ltda.</strong>
            </div>
          </div>

          <div className="ph-body">
            <div className="ph-step ph-card">
              <span className="ph-tag">
                <FileText size={14} /> Duplicata nº 4821
              </span>
              <p className="ph-label">Cliente Exemplo S.A.</p>
              <p className="ph-value">{brl(amount)}</p>
              <p className="ph-meta">Vencimento em 90 dias</p>
              <span className="ph-cta">Antecipar agora</span>
            </div>

            <div className="ph-step ph-progress">
              <span className="ph-tag">
                <Lock size={14} /> Operação segura
              </span>
              <p className="ph-label">Analisando a duplicata</p>
              <div className="ph-bar">
                <i />
              </div>
              <p className="ph-meta">Assinatura digital do contrato</p>
            </div>

            <div className="ph-step ph-done">
              <span className="ph-check">
                <Check size={22} strokeWidth={2.5} />
              </span>
              <p className="ph-label">Recebido à vista</p>
              <p className="ph-value ph-amount">{brl(amount)}</p>
              <p className="ph-meta">Disponível na sua conta</p>
            </div>
          </div>

          <div className="ph-list">
            <p className="ph-list-title">Próximos vencimentos</p>
            <div className="ph-row">
              <span>Duplicata nº 4807</span>
              <strong>R$ 12.364,90</strong>
            </div>
            <div className="ph-row">
              <span>Cheque nº 0219</span>
              <strong>R$ 7.185,00</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="float float-left" aria-hidden="true">
        <span className="float-icon">
          <Check size={16} strokeWidth={2.5} />
        </span>
        <div>
          <strong>Contrato assinado</strong>
          <small>100% digital, sem papel</small>
        </div>
      </div>
      <div className="float float-right" aria-hidden="true">
        <span className="float-icon">
          <FileText size={16} />
        </span>
        <div>
          <strong>Antecipação solicitada</strong>
          <small>Duplicata nº 4821</small>
        </div>
      </div>

      <figcaption className="phone-note">Ilustração com dados fictícios</figcaption>
    </figure>
  );
}
